// V2 Templates — real, complete decks (not prompts). Each card shows a live
// cover thumbnail (themed to the deck), opens a full navigable preview, and
// "Use template" clones it into a new editable deck in the Studio. Light DOM so
// the shared CSS + mountThumbs apply. Reuses home.css .tpl-* + the gallery's
// dark .gal-modal lightbox.

import { LitElement, html, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { navigate } from '../lib/router-state';
import { mountThumbs } from '../lib/thumb';
import { renderSlidesDoc } from '../lib/render-slides';
import { TEMPLATE_DECKS, templateById, type DeckTemplate } from '../lib/template-decks';
import { api } from '../api/client';
import { setPageTitle } from '../lib/page-title';
import type { DeckModel } from '../lib/deck-model';

const CATEGORIES = ['All', 'Board', 'Sales', 'Product', 'Internal'] as const;

@customElement('templates-page')
export class TemplatesPage extends LitElement {
  @state() private category: string = 'All';
  @state() private previewId = '';
  @state() private busy = false;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  connectedCallback(): void {
    super.connectedCallback();
    setPageTitle('Templates', 'Curated starting points for your next Folio deck.');
  }

  private get shown(): DeckTemplate[] {
    return this.category === 'All'
      ? TEMPLATE_DECKS
      : TEMPLATE_DECKS.filter((t) => t.category === this.category);
  }

  protected updated(): void {
    // Live cover-slide thumbnails. The host carries the deck's own theme/brand
    // so the preview tile reads true to the template, not the app chrome.
    mountThumbs(this, (id) => (id.startsWith('tpl:') ? templateById(id.slice(4))?.deck.slides[0] : undefined));
  }

  /** Clone the template into a new owned deck, then open it in the Studio. */
  private async use(t: DeckTemplate): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      const deck = t.deck;
      const notes = deck.notes || {};
      const created = await api.createDeck({
        title: deck.meta.title,
        subtitle: deck.meta.subtitle,
        html: renderSlidesDoc(deck),
        speakerNotes: deck.slides.map((s) => notes[s.id] || ''),
        model: deck as unknown as DeckModel,
        status: 'draft',
      });
      navigate(`/studio?deck=${encodeURIComponent(created.id)}`);
    } catch (err) {
      console.error('Failed to use template', err);
      this.busy = false;
    }
  }

  private renderPreview(): TemplateResult {
    const t = templateById(this.previewId);
    if (!t) return html``;
    return html`
      <div class="scrim" @mousedown=${(e: MouseEvent) => { if (e.target === e.currentTarget) this.previewId = ''; }}>
        <div class="gal-modal" role="dialog" aria-modal="true" aria-label=${`${t.name} preview`}>
          <div class="gal-modal-stage">
            <iframe
              class="tpl-preview-frame"
              .srcdoc=${renderSlidesDoc(t.deck)}
              sandbox="allow-scripts allow-popups allow-forms allow-modals"
              title=${`${t.name} preview`}></iframe>
          </div>
          <div class="gal-modal-bar">
            <div class="gal-modal-info">
              <div class="gal-modal-name">${t.name}</div>
              <div class="gal-modal-desc faint">${t.deck.slides.length} slides · ${t.category}</div>
            </div>
            <div class="gal-modal-actions">
              <button class="btn btn--ghost" @click=${() => { this.previewId = ''; }}>Close</button>
              <button class="btn btn--ai" ?disabled=${this.busy} @click=${() => this.use(t)}>Use this template</button>
            </div>
          </div>
        </div>
      </div>`;
  }

  render(): TemplateResult {
    return html`
      <div class="home">
        <section class="tpl-hero">
          <div class="hero-eyebrow">✦ Templates</div>
          <h1 class="tpl-hero-title">Start from a proven shape.</h1>
          <p class="tpl-hero-sub">Each template is a complete, real deck — preview it in full, then use it to start an editable copy. Nothing here is locked.</p>
        </section>
        <div class="tpl-filters">
          ${CATEGORIES.map((c) => html`
            <button class="chip ${this.category === c ? 'is-active' : ''}" @click=${() => { this.category = c; }}>${c}</button>`)}
        </div>
        <div class="tpl-gallery">
          ${this.shown.map((t) => html`
            <div class="tpl-card">
              <button class="tpl-thumb-btn" @click=${() => { this.previewId = t.id; }} aria-label=${`Preview ${t.name}`}>
                <div
                  class="tpl-thumb"
                  data-thumb=${`tpl:${t.id}`}
                  data-theme=${t.deck.meta.theme || 'workplace-paper'}
                  style=${t.deck.meta.brand ? `--brand-color:${t.deck.meta.brand}` : ''}></div>
              </button>
              <div class="tpl-meta">
                <div class="tpl-name-row"><span class="tpl-name">${t.name}</span><span class="badge badge--brand">${t.category}</span></div>
                <div class="tpl-desc">${t.blurb}</div>
                <div class="tpl-actions">
                  <button class="btn btn--ghost btn--sm" @click=${() => { this.previewId = t.id; }}>Preview</button>
                  <button class="btn btn--primary btn--sm" ?disabled=${this.busy} @click=${() => this.use(t)}>Use template</button>
                </div>
              </div>
            </div>`)}
        </div>
        ${this.previewId ? this.renderPreview() : ''}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'templates-page': TemplatesPage }
}
